/**
 * DSCR pricing tab of the consolidated Admin → Pricing screen.
 *
 * Backend: `/admin/data/dscr-pricing/*` (app/routers/admin_data.py); contract in
 * frontend_documentation/guidelines-and-dscr-pricing-admin.md.
 *
 * One versioned price sheet per counterparty, blob-backed: the ORIGINAL grid is
 * always kept as `source.xlsx` alongside the parsed `pricing.json`. The platform
 * base sheet is `resfin-base` (owner 2026-10-08); `eastview-base` stays through
 * the continuity gap.
 *
 * Parsed `pricing` shape (from parse_pricer_xlsx):
 *   tierMargins[]  {tier, margin}                    — bond points
 *   ladders{}      tier → [{coupon, spread_-_5yr, 5_1_arm, 7_1_arm, fixed_30}]
 *   llpaTables[]   {table, rows: [{label, values}]}  — FICO buckets / Adjustments
 *   minMaxPrices[] fieldOptions{}                    — row labels → option lists
 *   guidelineOverlays[]                              — agent-mapped, editable
 *
 * Two ways in: the import wizard (parse → review → save) and the diff review
 * (upload a NEW workbook, see per-coupon / LLPA / margin deltas against the
 * stored current version, then commit it). Neither writes until you say so.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import CompareArrowsOutlined from "@mui/icons-material/CompareArrowsOutlined";
import RefreshRounded from "@mui/icons-material/Refresh";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import { useSnackbar } from "notistack";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { adminDscrPricingApi } from "../../../api/adminDataApi";
import { A } from "../shared/adminTokens";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { formatDate } from "../../../_src_shared/utils/functions/formatDate";
import PricingEditLog from "./PricingEditLog";
import DscrPricingTables, { DSCR_SECTIONS } from "./DscrPricingTables";
import DscrImportWizard from "./DscrImportWizard";
import DscrDiffDialog from "./DscrDiffDialog";
import DscrAgentReviewDialog from "./DscrAgentReviewDialog";
import {
  FormulaBanner,
  MetaChip,
  PricingField,
  PricingPageLayout,
  PricingSection,
  SectionEmpty,
  T,
} from "./pricingUi";

/**
 * Rail entries — the counterparty picker and history bracket the editable ones.
 * The editable sections all render the sheet's current version, so each carries
 * it as its rail annotation.
 */
const dscrRail = (version) => [
  { id: "dscr-counterparty", label: "Counterparty" },
  ...DSCR_SECTIONS.map((s) => ({
    ...s,
    meta: version != null ? `v${version}` : undefined,
  })),
  { id: "dscr-history", label: "Version history" },
];

const FORMULA = [
  { chip: "coupon ladder" },
  { text: "Fixed-30 / 5-1 / 7-1" },
  { op: "−" },
  { chip: "tier margin" },
  { op: "−" },
  { chip: "LLPA stack" },
];

/**
 * The admin section's button pair. The theme's default `<Button>` is dark navy,
 * so these set background AND text colour together (never one without the
 * other — see the button rule in CLAUDE.md).
 */
const BTN_BASE = {
  height: 34,
  borderRadius: "8px",
  px: 1.75,
  fontSize: 12.5,
  fontWeight: 600,
  textTransform: "none",
  whiteSpace: "nowrap",
  boxShadow: "none",
};

const PRIMARY_BTN_SX = {
  ...BTN_BASE,
  bgcolor: A.teal,
  color: "#fff",
  boxShadow: "0 1px 2px rgba(18,134,154,.35)",
  "&:hover": {
    bgcolor: A.tealDark,
    boxShadow: "0 2px 8px rgba(18,134,154,.35)",
  },
};

const GHOST_BTN_SX = {
  ...BTN_BASE,
  bgcolor: A.surface,
  color: A.ink2,
  border: `1px solid ${A.lineStrong}`,
  "&:hover": {
    bgcolor: A.tealSoft,
    color: A.tealDeep,
    borderColor: A.teal,
  },
};

const errText = (err, fallback) =>
  err?.response?.data?.detail || err?.message || fallback;

export default function DscrPricingPane({ cpId }) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [list, setList] = useState(null);
  const [doc, setDoc] = useState(null);
  const [log, setLog] = useState([]);
  // `GET /{cpId}/days` — null when it failed (the strip just doesn't render).
  const [sheetDays, setSheetDays] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [busy, setBusy] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);

  const fetchList = useCallback(() => {
    setLoadingList(true);
    adminDscrPricingApi
      .list()
      .then(setList)
      .catch((err) => {
        setList([]);
        enqueueSnackbar(errText(err, "Couldn't load counterparty sheets"), {
          variant: "error",
        });
      })
      .finally(() => setLoadingList(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    if (!cpId && list?.length) {
      navigate(`/admin/pricing/dscr/${list[0].cpId}`, { replace: true });
    }
  }, [cpId, list, navigate]);

  const loadDoc = useCallback(
    (id, version) => {
      if (!id) return;
      setLoadingDoc(true);
      Promise.all([
        adminDscrPricingApi.get(id, version),
        adminDscrPricingApi.log(id).catch(() => []),
        adminDscrPricingApi
          .days(id)
          .then((r) => r?.days || [])
          .catch(() => null),
      ])
        .then(([d, l, days]) => {
          setDoc(d);
          setLog(l);
          setSheetDays(days);
        })
        .catch((err) => {
          setDoc(null);
          enqueueSnackbar(errText(err, "Couldn't load counterparty pricing"), {
            variant: "error",
          });
        })
        .finally(() => setLoadingDoc(false));
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    loadDoc(cpId);
  }, [cpId, loadDoc]);

  const isHistoricalView =
    doc?.requestedVersion != null && doc.requestedVersion !== doc.currentVersion;

  // Save an edited `pricing` object as a new version.
  const savePricing = useCallback(
    (pricing, note) => {
      if (!cpId) return Promise.resolve();
      setBusy(true);
      return adminDscrPricingApi
        .save(cpId, { pricing, note: note || "edited in admin" })
        .then((out) => {
          enqueueSnackbar(`Saved as v${out?.version}`, { variant: "success" });
          loadDoc(cpId);
          fetchList();
        })
        .catch((err) => {
          enqueueSnackbar(errText(err, "Save failed"), { variant: "error" });
          throw err;
        })
        .finally(() => setBusy(false));
    },
    [cpId, enqueueSnackbar, loadDoc, fetchList],
  );

  const handleRevert = useCallback(
    (toVersion) => {
      if (!cpId) return;
      if (
        !window.confirm(
          `Revert ${cpId} to v${toVersion}? It is copied forward as a NEW current version — nothing is deleted.`,
        )
      )
        return;
      setBusy(true);
      adminDscrPricingApi
        .revert(cpId, { toVersion, note: `reverted to v${toVersion}` })
        .then((out) => {
          enqueueSnackbar(`Reverted — now v${out?.version}`, {
            variant: "success",
          });
          loadDoc(cpId);
          fetchList();
        })
        .catch((err) =>
          enqueueSnackbar(errText(err, "Revert failed"), { variant: "error" }),
        )
        .finally(() => setBusy(false));
    },
    [cpId, enqueueSnackbar, loadDoc, fetchList],
  );

  const versions = useMemo(() => doc?.versions || [], [doc]);
  const versionRows = useMemo(
    () =>
      versions
        .slice()
        .sort((a, b) => (b.v || 0) - (a.v || 0))
        .map((v) => ({
          at: v.createdAt,
          by: v.createdBy,
          action: v.action,
          toVersion: v.v,
          fromVersion: v.sourceVersion ?? null,
          note: v.note,
          asOfDate: v.asOfDate,
        })),
    [versions],
  );

  const shownVersion = doc?.requestedVersion ?? doc?.currentVersion;

  return (
    <PricingPageLayout nav={dscrRail(shownVersion)}>
      <FormulaBanner
        lead="DSCR pricing ="
        parts={FORMULA}
        tail="→ counterparty sheets map onto the same variables"
      />

      {/* Counterparty picker. A single-column list of sheets — navigation
          chrome, not a data table, so deliberately not an AG Grid. */}
      <PricingSection
        id="dscr-counterparty"
        title="Counterparty"
        desc="Each counterparty carries its own versioned sheet. The original workbook is kept beside the parsed pricing, so a version can always be traced back to the grid it came from."
        // The sheet picker is how you get anywhere else on this tab — folding it
        // away would hide the page's own navigation, so it doesn't collapse and
        // its actions sit inline at the top right instead of on their own row.
        collapsible={false}
        actions={
          <Flx ac g={1} wrap>
            <Tooltip title="Refresh">
              <span>
                <IconButton
                  size="small"
                  onClick={fetchList}
                  disabled={loadingList}
                >
                  <RefreshRounded fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {/* Ghost + teal primary, the admin section's button pair. Both set
                background AND text colour together, per the theme rule. */}
            <Button
              size="small"
              startIcon={<AutoAwesomeOutlined />}
              onClick={() => setAgentOpen(true)}
              disabled={!cpId || busy}
              sx={GHOST_BTN_SX}
            >
              Run agent review
            </Button>
            <Button
              size="small"
              startIcon={<CompareArrowsOutlined />}
              onClick={() => setDiffOpen(true)}
              disabled={!cpId || busy}
              sx={GHOST_BTN_SX}
            >
              Diff a new workbook
            </Button>
            <Button
              size="small"
              startIcon={<UploadFileOutlined />}
              onClick={() => setWizardOpen(true)}
              sx={PRIMARY_BTN_SX}
            >
              Add DSCR pricing
            </Button>
          </Flx>
        }
      >
        {list && list.length === 0 ? (
          <SectionEmpty>
            No counterparty sheets yet. Import a workbook to create the first
            one.
          </SectionEmpty>
        ) : (
          <Flx g={1} wrap sx={{ mb: doc ? 2 : 0 }}>
            {(list || []).map((c) => {
              const active = c.cpId === cpId;
              return (
                <Box
                  key={c.cpId}
                  onClick={() => navigate(`/admin/pricing/dscr/${c.cpId}`)}
                  sx={{
                    px: 2,
                    py: 1.25,
                    minWidth: 170,
                    cursor: "pointer",
                    borderRadius: "10px",
                    // The selected sheet is a filled teal tile with a 1.5px
                    // edge — the border thickens rather than a ring being added,
                    // so the tile doesn't jump by a pixel when it's picked.
                    border: `1.5px solid ${active ? A.teal : A.line}`,
                    bgcolor: active ? A.tealSoft : A.surface,
                    transition: "border-color .13s, background .13s",
                    "&:hover": {
                      borderColor: active ? A.teal : A.lineStrong,
                      bgcolor: active ? A.tealSoft : A.bg,
                    },
                  }}
                >
                  <Flx column g={0.25}>
                    <Txt
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: active ? A.ink : A.ink2,
                      }}
                    >
                      {c.counterparty || c.cpId}
                    </Txt>
                    <Txt
                      sx={{
                        fontSize: 11,
                        color: active ? A.tealDeep : A.muted,
                      }}
                    >
                      {[`v${c.currentVersion ?? "—"}`, c.status]
                        .filter(Boolean)
                        .join(" · ")}
                    </Txt>
                  </Flx>
                </Box>
              );
            })}
          </Flx>
        )}

        {doc ? (
          <Flx
            ac
            jb
            g={1.5}
            wrap
            sx={{ pt: 2, borderTop: `1px solid ${T.lineSoft}` }}
          >
            <Flx column g={0.5}>
              <Flx ac g={1} wrap>
                <Txt sx={{ fontSize: 15, fontWeight: 700, color: T.ink }}>
                  {doc.counterparty || doc.cpId}
                </Txt>
                {doc.status ? (
                  <Chip
                    size="small"
                    label={doc.status}
                    sx={{
                      height: 20,
                      fontSize: 10.5,
                      fontWeight: 700,
                      borderRadius: "20px",
                      bgcolor: A.okSoft,
                      color: A.ok,
                    }}
                  />
                ) : null}
                {isHistoricalView ? (
                  <Chip
                    size="small"
                    label="viewing a prior version — read only"
                    sx={{
                      height: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: "#fef3c7",
                      color: "#92400e",
                    }}
                  />
                ) : null}
              </Flx>
              <Txt sx={{ fontSize: 11.5, color: T.muted }}>
                {[
                  doc.contact?.name,
                  doc.contact?.email,
                  doc.version?.asOfDate
                    ? `sheet as of ${formatDate(doc.version.asOfDate, "", "MMM d, yyyy")}`
                    : null,
                  doc.version?.createdBy
                    ? `last edit by ${doc.version.createdBy}`
                    : null,
                  doc.version?.createdAt
                    ? formatDateTime(doc.version.createdAt)
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Txt>
            </Flx>
            <PricingField label="Version" sx={{ width: 200 }}>
              <TextField
                select
                size="small"
                value={shownVersion ?? ""}
                onChange={(e) => loadDoc(cpId, Number(e.target.value))}
                fullWidth
              >
                {versions
                  .slice()
                  .sort((a, b) => (b.v || 0) - (a.v || 0))
                  .map((v) => (
                    <MenuItem key={v.v} value={v.v}>
                      v{v.v}
                      {v.asOfDate
                        ? ` · ${formatDate(v.asOfDate, "", "MMM d")}`
                        : ""}
                      {v.v === doc.currentVersion ? " (current)" : ""}
                    </MenuItem>
                  ))}
              </TextField>
            </PricingField>
          </Flx>
        ) : null}
      </PricingSection>

      {!cpId ? null : loadingDoc && !doc ? (
        <PricingSection>
          <Flx center sx={{ py: 6 }}>
            <CircularProgress size={22} />
          </Flx>
        </PricingSection>
      ) : !doc ? (
        <PricingSection>
          <SectionEmpty>Couldn&apos;t load this counterparty.</SectionEmpty>
        </PricingSection>
      ) : (
        <DscrPricingTables
          // Remount on a version switch so the draft reseeds from the new doc.
          key={`${cpId}-${shownVersion}`}
          pricing={doc.pricing}
          readOnly={isHistoricalView || busy}
          version={shownVersion != null ? `v${shownVersion}` : undefined}
          onSave={savePricing}
        />
      )}

      <PricingSection
        id="dscr-history"
        title="Version history"
        desc="Every save writes a new immutable version. Reverting copies an old version forward as the new current one — nothing is deleted."
        dense
      >
        {/* The days strip is the version table's caption: one inset for
            both, the table flush under it, laid out like the Edit log below. */}
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          <SheetDaysStrip
            days={sheetDays}
            versions={versions}
            shownVersion={shownVersion}
            onOpenVersion={(v) => loadDoc(cpId, v)}
          />
          <PricingEditLog
            flush
            rows={versionRows}
            onRevert={handleRevert}
            asOfColumn
            empty="No versions recorded."
          />
        </Box>
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          <Txt
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: T.ink,
              display: "block",
              mb: 1,
            }}
          >
            Edit log
          </Txt>
          {/* The wrapping Box supplies the inset for this one. */}
          <PricingEditLog flush rows={log} empty="No edits logged." />
        </Box>
      </PricingSection>

      <DscrImportWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSaved={(savedCpId) => {
          setWizardOpen(false);
          fetchList();
          if (savedCpId) navigate(`/admin/pricing/dscr/${savedCpId}`);
        }}
      />

      <DscrDiffDialog
        open={diffOpen}
        cpId={cpId}
        counterparty={doc?.counterparty}
        onClose={() => setDiffOpen(false)}
        onCommit={(pricing, note) =>
          savePricing(pricing, note).then(() => setDiffOpen(false))
        }
      />

      <DscrAgentReviewDialog
        open={agentOpen}
        cpId={cpId}
        version={shownVersion}
        onClose={() => setAgentOpen(false)}
      />
    </PricingPageLayout>
  );
}

DscrPricingPane.propTypes = {
  cpId: PropTypes.string,
};

const dayLabel = (d) => formatDate(d, d, "MMM d, yyyy");

/**
 * Daily sheet tracking — the days this counterparty's sheet was captured
 * (`GET /dscr-pricing/{cpId}/days`, newest first). Collapsed it is the count +
 * the latest day; expanded, one pill per day carrying the newest version
 * stamped with that `asOfDate`, which opens that version. A day with no
 * matching version (captured before the version carried its day) is shown
 * but not clickable. Renders nothing when the days call failed.
 */
const SheetDaysStrip = ({ days, versions, shownVersion, onOpenVersion }) => {
  const [expanded, setExpanded] = useState(false);

  // day → its versions, newest first (several saves can land on one day; the
  // daily blob holds the last of them).
  const byDay = useMemo(() => {
    const m = {};
    versions
      .filter((v) => v.asOfDate)
      .sort((a, b) => (b.v || 0) - (a.v || 0))
      .forEach((v) => {
        (m[v.asOfDate] = m[v.asOfDate] || []).push(v.v);
      });
    return m;
  }, [versions]);

  if (!Array.isArray(days)) return null;

  // Sits directly on the (flush) version table, so it supplies the small gap
  // above it — the caller owns the horizontal inset.
  return (
    <Box sx={{ mb: 1 }}>
      <Flx ac g={1} wrap>
        <Txt sx={{ fontSize: 12, fontWeight: 600, color: T.ink }}>
          Captured sheet days
        </Txt>
        <MetaChip>
          {days.length} day{days.length === 1 ? "" : "s"}
        </MetaChip>
        {days.length ? (
          <Txt sx={{ fontSize: 12, color: T.muted }}>
            latest {dayLabel(days[0])}
          </Txt>
        ) : (
          <Txt sx={{ fontSize: 12, color: T.muted }}>
            none yet — versions saved before daily tracking carry no day
          </Txt>
        )}
        {days.length ? (
          <Button
            size="small"
            variant="text"
            onClick={() => setExpanded((x) => !x)}
            sx={{ ml: "auto", fontSize: 12, py: 0.25 }}
          >
            {expanded ? "Hide days" : "Show all days"}
          </Button>
        ) : null}
      </Flx>

      {expanded ? (
        <Flx g={0.75} wrap sx={{ mt: 1.25 }}>
          {days.map((d) => {
            const vs = byDay[d] || [];
            const shown = vs.includes(shownVersion);
            const label = vs.length
              ? `${dayLabel(d)} · v${vs[0]}${vs.length > 1 ? ` +${vs.length - 1}` : ""}`
              : dayLabel(d);
            const pill = (
              <ButtonBase
                key={d}
                disabled={!vs.length}
                onClick={() => onOpenVersion(vs[0])}
                sx={{
                  px: 1.25,
                  py: 0.5,
                  borderRadius: "6px",
                  fontFamily: T.mono,
                  fontSize: 11,
                  color: shown ? T.accentInk : T.ink2,
                  bgcolor: shown ? T.accentSoft2 : T.surface,
                  border: `1px solid ${shown ? T.accent : T.line}`,
                  "&:hover": { borderColor: T.lineStrong, bgcolor: T.surface2 },
                  "&.Mui-disabled": { color: T.faint },
                  "&.Mui-focusVisible": { outline: `2px solid ${T.accent}` },
                }}
              >
                {label}
              </ButtonBase>
            );
            return vs.length ? (
              <Tooltip
                key={d}
                title={`Open v${vs[0]}${
                  vs.length > 1 ? ` — also saved that day: v${vs.slice(1).join(", v")}` : ""
                }`}
              >
                {pill}
              </Tooltip>
            ) : (
              pill
            );
          })}
        </Flx>
      ) : null}
    </Box>
  );
};

SheetDaysStrip.propTypes = {
  days: PropTypes.array,
  versions: PropTypes.array,
  shownVersion: PropTypes.number,
  onOpenVersion: PropTypes.func,
};
